/**
 * Een workspace archiveren, verwijderen en terugzetten, vanaf de client.
 * De server (`apps/worker/src/db/workspaceArchive.ts`) levert en ontvangt
 * het archief per tabel in pagina's; hier worden die tot één bestand
 * samengevoegd en weer opgeknipt. Zo blijft elk verzoek klein.
 */
import type { RequestOptions } from "./http";

interface Requester {
  request<T>(method: string, path: string, opts?: RequestOptions): Promise<T>;
}

export type ArchiveRow = Record<string, unknown>;

export interface ArchiveManifest {
  format: "arch-designer-workspace-archive";
  version: number;
  schema: string | null;
  exported_at: string;
  workspace: { id: string; name: string };
  tables: Array<{ table: string; rows: number }>;
}

/** Het bestand dat de gebruiker bewaart: manifest plus alle rijen. */
export interface WorkspaceArchive extends ArchiveManifest {
  data: Record<string, ArchiveRow[]>;
}

export interface ArchiveProgress {
  phase: "download" | "rows" | "links" | "finish";
  table?: string;
  done: number;
  total: number;
}

export interface RestoreResult {
  ok: boolean;
  mismatches: Array<{ table: string; expected: number; actual: number }>;
}

/** Zoveel rijen per verzoek; de server staat er niet meer toe. */
const PAGE = 200;
const BASE = "/api/v1/super-admin/archive";
const enc = encodeURIComponent;

export async function downloadWorkspaceArchive(
  req: Requester,
  workspaceId: string,
  onProgress?: (p: ArchiveProgress) => void,
): Promise<WorkspaceArchive> {
  const manifest = await req.request<ArchiveManifest>(
    "GET",
    `${BASE}/${enc(workspaceId)}/manifest`,
  );
  const total = manifest.tables.reduce((n, t) => n + t.rows, 0);
  const data: Record<string, ArchiveRow[]> = {};
  let done = 0;
  for (const { table, rows } of manifest.tables) {
    const out: ArchiveRow[] = [];
    for (let offset = 0; offset < rows; offset += PAGE) {
      const page = await req.request<{ rows: ArchiveRow[] }>(
        "GET",
        `${BASE}/${enc(workspaceId)}/rows`,
        { query: { table, offset, limit: PAGE } },
      );
      out.push(...page.rows);
      done += page.rows.length;
      onProgress?.({ phase: "download", table, done, total });
      if (page.rows.length === 0) break;
    }
    if (out.length !== rows) {
      throw new Error(
        `Archive of '${table}' is incomplete (${out.length} of ${rows} rows). Was the workspace changed during the download? Try again.`,
      );
    }
    data[table] = out;
  }
  return { ...manifest, data };
}

/** Een gekozen bestand: is het een archief, en wat zit erin? */
export function parseWorkspaceArchive(text: string): WorkspaceArchive {
  const a = JSON.parse(text) as Partial<WorkspaceArchive>;
  if (a.format !== "arch-designer-workspace-archive" || !a.workspace?.id || !a.data || !a.tables) {
    throw new Error("This file is not an Arch-designer workspace archive.");
  }
  return a as WorkspaceArchive;
}

/** Bestandsnaam voor het archief: naam, datum. */
export function archiveFileName(archive: ArchiveManifest): string {
  const slug =
    archive.workspace.name.replace(/[^\p{L}\p{N}]+/gu, "-").replace(/^-|-$/g, "") || "workspace";
  return `arch-designer-${slug}-${archive.exported_at.slice(0, 10)}.json`;
}

export function deleteWorkspace(
  req: Requester,
  workspaceId: string,
  confirmName: string,
): Promise<{ deleted: string }> {
  return req.request("DELETE", `${BASE}/${enc(workspaceId)}`, {
    body: { confirm_name: confirmName },
  });
}

/** Mag dit archief hier terug? Gooit met de reden van de server. */
export function checkWorkspaceRestore(
  req: Requester,
  archive: WorkspaceArchive,
): Promise<{ ok: true; workspace: { id: string; name: string } }> {
  return req.request("POST", `${BASE}/restore/check`, { body: { manifest: manifestOf(archive) } });
}

/**
 * Zet het archief terug: eerst alle rijen (ouders eerst), dan de
 * verwijzingen, dan een telling. Mislukt het halverwege, dan wordt de
 * half teruggezette workspace weer verwijderd, zodat je opnieuw kunt.
 */
export async function restoreWorkspaceArchive(
  req: Requester,
  archive: WorkspaceArchive,
  onProgress?: (p: ArchiveProgress) => void,
): Promise<RestoreResult> {
  const manifest = manifestOf(archive);
  await checkWorkspaceRestore(req, archive);
  const total = archive.tables.reduce((n, t) => n + (archive.data[t.table]?.length ?? 0), 0);
  let started = false;
  try {
    for (const phase of ["rows", "links"] as const) {
      let done = 0;
      for (const { table } of archive.tables) {
        const rows = archive.data[table] ?? [];
        for (let i = 0; i < rows.length; i += PAGE) {
          const chunk = rows.slice(i, i + PAGE);
          await req.request("POST", `${BASE}/restore/rows`, {
            body: { workspace_id: archive.workspace.id, table, phase, rows: chunk },
          });
          started = true;
          done += chunk.length;
          onProgress?.({ phase, table, done, total });
        }
      }
    }
    onProgress?.({ phase: "finish", done: total, total });
    return await req.request<RestoreResult>("POST", `${BASE}/restore/finish`, {
      body: { manifest },
    });
  } catch (err) {
    if (started) {
      await deleteWorkspace(req, archive.workspace.id, archive.workspace.name).catch(
        () => undefined,
      );
    }
    throw err;
  }
}

function manifestOf(archive: WorkspaceArchive): ArchiveManifest {
  const { data: _data, ...manifest } = archive;
  return manifest;
}
